// Virtual-keyboard avoidance for coarse-pointer (touch) devices.
//
// The workspace had ZERO soft-keyboard handling — no visualViewport reader
// anywhere — so on iPad the on-screen keyboard covers the lower ~40% of the
// LAYOUT viewport and occludes bottom-anchored inputs (the canvas AI prompt,
// the chat composer). Those inputs live in NON-scrolling containers, so iOS
// can't auto-scroll them into view the way it does for inputs inside a normal
// scroll region.
//
// This installs a single window.visualViewport listener that measures how much
// of the layout viewport the keyboard covers and publishes it as a CSS custom
// property on <html> — `--kbd-inset` (px). Bottom-anchored composers translate
// up by that amount via `transform: translateY(calc(-1 * var(--kbd-inset,0px)))`
// so they sit just above the keyboard. Publishing a CSS var on documentElement
// from a boot helper mirrors the shell's --shell-top / canvasBackground pattern
// (the single established way this app threads viewport chrome into CSS).
//
// Idempotent singleton — safe to call from every composer's onInit / onCreate.

import { isCoarsePointer } from './pointerCapability.js'

// FA204 / frank trap — `_installed` is genuine cross-call state (this file
// exposes no `el`/`root` argument to hang it on, and it must survive across
// every independent caller — canvas prompt, chat composer, …) so it's
// re-homed onto `window.__keyboardAvoidanceInstalled` rather than staying a
// bare module-scope let (see pointerCapability.js's `window.__pointerCapabilityState`
// for the identical shape of fix). `KBD_INSET_VAR`/`_isTextEntry` were pure
// and had exactly one caller, so they're inlined below instead.

// Wire the visualViewport listeners once. Every subsequent call is a no-op, so
// both the canvas prompt and the chat composer can call this from their own
// lifecycle without racing.
export const installKeyboardAvoidance = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (window.__keyboardAvoidanceInstalled) return
  const vv = window.visualViewport
  if (typeof vv === 'undefined' || !vv) return
  window.__keyboardAvoidanceInstalled = true

  const KBD_INSET_VAR = '--kbd-inset'

  // The soft keyboard is only up when a text-entry field owns focus. Anything
  // else (a button, the canvas surface, a scroll container) means the
  // keyboard is down, so the inset must collapse to 0 — otherwise a residual
  // visualViewport offset (pinch-zoom, URL-bar collapse) would lift the
  // composer for no reason.
  const isTextEntry = (node) => {
    if (!node || node.nodeType !== 1) return false
    const tag = node.tagName
    if (tag === 'TEXTAREA') return true
    if (tag === 'INPUT') {
      // Non-text inputs (checkbox, button, range, file…) don't raise a keyboard.
      const type = (node.getAttribute('type') || 'text').toLowerCase()
      return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file'].includes(
        type
      )
    }
    return node.isContentEditable === true
  }

  const root = document.documentElement

  const apply = () => {
    // Keyboard overlap = the slice of the layout viewport the visual viewport no
    // longer covers at the bottom. offsetTop folds in the pinch-zoom / pinned
    // top-bar case so we never over- or under-shoot.
    const overlap = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop))
    const lifted = overlap > 0 && isCoarsePointer() && isTextEntry(document.activeElement)
    root.style.setProperty(KBD_INSET_VAR, (lifted ? overlap : 0) + 'px')
  }

  vv.addEventListener('resize', apply)
  vv.addEventListener('scroll', apply)
  // focusin / focusout catch the field gaining or losing focus without a
  // viewport change — e.g. tabbing between two inputs while the keyboard stays
  // up, or blurring to dismiss it.
  window.addEventListener('focusin', apply)
  window.addEventListener('focusout', apply)

  apply()
}
